import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import {
  CERTIFICATION_PROGRAMS,
  DEMO_CERT_STATUSES,
  getProgramById,
  levelColor,
  statusColor,
  type CertificationLevel,
  type ProgramStatus,
} from "../lib/certifications";
import { fetchUserOverview } from "../lib/sentinel-api";
import { useIdentity } from "../lib/identity";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  const labels: Record<ProgramStatus, string> = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    expired: "Expired",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.25rem 0.625rem",
        borderRadius: "var(--radius-full)",
        fontSize: "0.6875rem",
        fontWeight: 600,
        background: `${statusColor(status)}15`,
        color: statusColor(status),
      }}
    >
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(status) }} />
      {labels[status]}
    </span>
  );
}

export default function MyProgramsPage() {
  const { identity } = useIdentity();
  const [userPrograms, setUserPrograms] = useState<typeof CERTIFICATION_PROGRAMS>([]);
  const [statuses, setStatuses] = useState<Record<string, ProgramStatus>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPrograms() {
      if (!identity.userId) {
        // Fallback to demo data
        const demoPrograms = CERTIFICATION_PROGRAMS.filter((p) =>
          DEMO_CERT_STATUSES.some((c) => c.program_id === p.id),
        );
        const demoStatuses: Record<string, ProgramStatus> = {};
        DEMO_CERT_STATUSES.forEach((c) => { demoStatuses[c.program_id] = c.status; });
        setUserPrograms(demoPrograms);
        setStatuses(demoStatuses);
        setLoading(false);
        return;
      }

      const overview = await fetchUserOverview(identity.userId);
      if (overview?.certifications && overview.certifications.length > 0) {
        const programIds = overview.certifications.map((c: { program_id: string; status: ProgramStatus }) => c.program_id);
        const programs = programIds
          .map((pid: string) => getProgramById(pid))
          .filter((p): p is NonNullable<typeof p> => Boolean(p));
        const statusMap: Record<string, ProgramStatus> = {};
        overview.certifications.forEach((c: { program_id: string; status: ProgramStatus }) => {
          statusMap[c.program_id] = c.status;
        });
        setUserPrograms(programs);
        setStatuses(statusMap);
      } else {
        // Fallback to demo data
        const demoPrograms = CERTIFICATION_PROGRAMS.filter((p) =>
          DEMO_CERT_STATUSES.some((c) => c.program_id === p.id),
        );
        const demoStatuses: Record<string, ProgramStatus> = {};
        DEMO_CERT_STATUSES.forEach((c) => { demoStatuses[c.program_id] = c.status; });
        setUserPrograms(demoPrograms);
        setStatuses(demoStatuses);
      }
      setLoading(false);
    }
    loadPrograms();
  }, [identity.userId]);

  if (loading) {
    return (
      <PlatformLayout>
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted" style={{ fontSize: "0.875rem" }}>Loading your programs...</p>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>My Programs</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Continue your training where you left off. All programs you're enrolled in are listed here.
        </p>
      </div>

      {userPrograms.length === 0 ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "1rem" }}>You haven't enrolled in any programs yet.</p>
          <Link to="/certifications" className="btn btn-primary">Browse Certifications</Link>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "var(--space-3)",
          }}
        >
          {userPrograms.map((program) => {
            const status = statuses[program.id] || "in_progress";
            return (
              <div key={program.id} className="card card-hover" style={{ overflow: "hidden", padding: 0, display: "flex", flexDirection: "column" }}>
                <div style={{ position: "relative", height: "120px", overflow: "hidden" }}>
                  <img src={program.image} alt={program.industry} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,22,40,0.1) 0%, rgba(10,22,40,0.7) 100%)" }} />
                  <div style={{ position: "absolute", bottom: "0.5rem", left: "0.75rem", right: "0.75rem" }}>
                    <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#fff" }}>{program.title}</div>
                  </div>
                </div>
                <div style={{ padding: "var(--space-3)", display: "flex", flexDirection: "column", flex: 1 }}>
                  <div style={{ display: "flex", gap: "0.375rem", marginBottom: "0.5rem" }}>
                    <LevelPill level={program.level} />
                    <StatusBadge status={status} />
                  </div>
                  <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem", flex: 1 }}>{program.description}</p>
                  <Link to={`/programs/${program.id}`} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>
                    {status === "not_started" ? "Start" : status === "completed" ? "Review" : "Continue"}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PlatformLayout>
  );
}
